import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';
import { Button, Card, Container, Section, Stack } from '@easymoving/ui';
import { useBackend } from '../lib/backend/context';
import { ItemForm, type ItemFormSubmit } from '../components/items/ItemForm';

export function AddItemPage() {
  const { t } = useTranslation();
  const backend = useBackend();
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState<string>();
  const [savedId, setSavedId] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0); // remount to reset on "add another"

  async function handleSubmit({ draft, file }: ItemFormSubmit) {
    setBusy(true);
    setBusyLabel(t('item.saving'));
    try {
      const itemId = await backend.createItem(draft);
      if (file) {
        setBusyLabel(t('item.uploadingPhoto'));
        await backend.uploadPrimaryImage(itemId, file);
      }
      setSavedId(itemId);
    } finally {
      setBusy(false);
      setBusyLabel(undefined);
    }
  }

  if (savedId) {
    return (
      <Container size="narrow" className="pt-8">
        <Card padding="lg" className="max-w-md mx-auto text-center">
          <Stack gap="md" className="items-center">
            <span className="inline-flex size-14 items-center justify-center rounded-full bg-olive-tint text-olive">
              <CheckCircle2 className="size-8" />
            </span>
            <h1 className="font-display text-2xl tracking-snug">{t('item.afterSaveTitle')}</h1>
            <Stack gap="sm" className="w-full">
              <Button
                size="lg"
                onClick={() => {
                  setSavedId(null);
                  setFormKey((k) => k + 1);
                }}
              >
                {t('item.addAnother')}
              </Button>
              <Button variant="secondary" size="lg" asChild>
                <Link to={`/app/items/${savedId}`}>{t('item.view')}</Link>
              </Button>
              <Button variant="ghost" asChild>
                <Link to="/app/items">{t('item.toGallery')}</Link>
              </Button>
            </Stack>
          </Stack>
        </Card>
      </Container>
    );
  }

  return (
    <Container size="narrow" className="pt-6">
      <Section title={t('item.addTitle')}>
        <div className="max-w-md mx-auto">
          <ItemForm
            key={formKey}
            busy={busy}
            busyLabel={busyLabel}
            submitLabel={t('item.save')}
            onSubmit={handleSubmit}
          />
        </div>
      </Section>
    </Container>
  );
}
